import { useEffect, useRef, type FC } from "react";
import { useLocation } from "react-router-dom";

import { closeNewCase, openNewCase, useNewCaseStore } from "../newCaseStore";
import { NewCaseDialog } from "./NewCaseDialog";

const isTyping = (target: EventTarget | null) =>
  target instanceof HTMLElement &&
  (target.isContentEditable ||
    ["INPUT", "TEXTAREA", "SELECT"].includes(target.tagName));

/** Another modal, menu or popover already has the keyboard. */
const overlayOpen = () =>
  Boolean(
    document.querySelector(
      '[role="dialog"][data-state="open"], [role="menu"][data-state="open"], [role="listbox"][data-state="open"]'
    )
  );

/**
 * The app-wide New Case modal: `openNewCase()` and the C key open it over the
 * current screen. Opening the new case (a route change) closes it.
 */
export const NewCaseDialogHost: FC = () => {
  const { open, clientId, session } = useNewCaseStore();
  const { pathname } = useLocation();
  const openedAt = useRef<string | null>(null);

  useEffect(() => {
    if (!open) openedAt.current = null;
    else if (openedAt.current === null) openedAt.current = pathname;
    else if (pathname !== openedAt.current) closeNewCase();
  }, [open, pathname]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "c" && e.key !== "C") return;
      if (e.metaKey || e.ctrlKey || e.altKey || e.shiftKey || e.repeat) return;
      if (e.defaultPrevented || isTyping(e.target) || overlayOpen()) return;
      e.preventDefault();
      openNewCase();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  return (
    <NewCaseDialog
      open={open}
      onOpenChange={(next) => {
        if (!next) closeNewCase();
      }}
      initialClientId={clientId}
      session={session}
    />
  );
};
